import React from 'react';
import * as Sentry from '@sentry/react';
import { useToast } from '@chakra-ui/react';

// Error fallback component
const ErrorFallback = ({ resetError }: { resetError: () => void }) => {
  const toast = useToast();

  // Show toast immediately when component mounts
  React.useEffect(() => {
    toast({
      title: 'An error occurred',
      description: 'Something went wrong. Our team has been notified.',
      status: 'error',
      duration: 5000,
      isClosable: true,
      position: 'bottom-right',
    });

    // Auto-reset after a short delay
    const timer = setTimeout(() => {
      resetError();
    }, 1000);

    return () => clearTimeout(timer);
  }, [resetError, toast]);

  // Return empty fragment - no visible UI change
  return null;
};

const SentryProvider = ({ children }: { children: React.ReactNode }) => {
  return (
    <Sentry.ErrorBoundary
      fallback={({ resetError }) => <ErrorFallback resetError={resetError} />}
      beforeCapture={(scope, error) => {
        // Add additional context
        scope.setTag('errorBoundary', true);
        scope.setExtra('errorDetails', String(error));
      }}
      showDialog={false} // Don't show Sentry's default error dialog
    >
      {children}
    </Sentry.ErrorBoundary>
  );
};

export default SentryProvider;
